// owner-map: REG-24
// Shared "generic LLM prompt" fetch — the fetch+parse core that four call
// sites (App.jsx submitCustomTrigger/answerChat/classifyAndDispatch,
// useSayerTour.js answerTourAside) each repeated verbatim: POST the prompt to
// /api/forecast/llm and return whichever of data.response / data.text the
// server populated (raw, untrimmed — callers trim/post-process differently).
//
// Deliberately NOT responsible for the AbortController lifecycle: callers
// mint their own controller, decide whether to register it in
// pendingAbortsRef, and release it in their own finally block. This helper
// only takes the resulting signal (optional) and forwards it to fetch, so a
// caller that never passes one (submitCustomTrigger) gets a timeout-only
// bounded fetch.

import { postLlm } from "../../services/forecastServerApi.js";
import { chatTurnHeaders } from "../chatTurn.js";

// Bound the call so a hung/slow backend can't hang the chat forever — the
// server-side sibling (`server/proxy/forecast.js` callBackendLlm) has had both a
// status check and this timeout; the client lacked both, so a stalled backend
// left the chat spinning indefinitely (#3524). Overridable via opts.timeoutMs.
//
// Raised from 15s: with web search enabled the model may make a grounding round trip before
// it answers, and 15s made that abort — the user saw "couldn't reach the assistant" on
// exactly the turns where searching was the point. The forecast server's own ceiling for this
// path is 120s (`llm_passthrough.DEFAULT_TIMEOUT`), so this stays well inside it.
const DEFAULT_TIMEOUT_MS = 45000;

// ── The web-search switch ────────────────────────────────────────────────────
// The forecast server attaches OpenRouter's native web-search plugin ONLY on its
// default-model path: `POST /llm` with no `model` gets `DEFAULT_MODEL` +
// `WEB_NATIVE_PLUGIN`, while a caller-supplied `model` is served search-free
// (`sooth_forecast_server/llm_passthrough.py::call_llm`). `LLMRequest` is `extra="forbid"`,
// so there is no `web: false` field to send — but that asymmetry is itself the switch:
// naming the SAME model the server would have chosen takes the caller-supplied branch and
// drops the plugin, leaving everything else identical.
//
// So search-off is a real switch, not a politely-worded prompt: the plugin is genuinely
// absent from the request, and the model cannot search even if it wanted to.
//
// COUPLING: these three constants MIRROR the server's DEFAULT_MODEL / DEFAULT_TEMPERATURE /
// DEFAULT_TIMEOUT. They must be sent TOGETHER — the caller-supplied branch passes
// `request.temperature` and `request.timeout` straight through, so omitting them would
// silently swap the provider's default sampling temperature (not 0.2) and a 60s ceiling into
// the search-off path, making it differ from search-on in more than search. If the server
// changes its default model, this pin goes stale and search-off runs the older slug; the
// covering test names that risk.
const NO_SEARCH_MODEL = "google/gemini-3.1-flash-lite";
const NO_SEARCH_TEMPERATURE = 0.2;
const NO_SEARCH_TIMEOUT_S = 120;

/**
 * @param {string} prompt
 * @param {{ signal?: AbortSignal | null, timeoutMs?: number, webSearch?: boolean, turnId?: string | null }} [opts]
 *   `turnId` ties the request to the chat turn that made it (lib/chatTurn.js); omit outside one.
 *   `webSearch` defaults to true (the historical behaviour — chatboard has always sent a
 *   bare `{prompt}` and so has always been on the server's search-enabled default path).
 * @returns {Promise<string>}
 */
export async function callLlm(
  prompt,
  { signal, timeoutMs = DEFAULT_TIMEOUT_MS, webSearch = true, turnId = null } = {},
) {
  // Abort on EITHER the caller's cancel (CLEAR/reset) OR the timeout. The server sibling
  // lets a caller signal REPLACE the timeout, but every client call site passes its own
  // signal — so precedence would silently disable the timeout here. `AbortSignal.any`
  // keeps both live; a timeout fires without the caller's signal being aborted, so the
  // caller's post-await `signal.aborted` check still distinguishes cancel from timeout.
  const timeout = AbortSignal.timeout(timeoutMs);
  const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;
  const body = webSearch
    ? { prompt }
    : {
        prompt,
        model: NO_SEARCH_MODEL,
        temperature: NO_SEARCH_TEMPERATURE,
        timeout: NO_SEARCH_TIMEOUT_S,
      };
  const r = await postLlm(body, { headers: chatTurnHeaders(turnId), signal: combined });
  // A non-2xx used to fall through to `r.json()` and silently return "" — indistinguishable
  // from a genuine empty reply. Throw so callers (all wrapped in try/catch) can surface it.
  if (!r.ok) throw new Error(`forecast /llm returned ${r.status}`);
  const data = await r.json();
  return data.response || data.text || "";
}
